// ============================================
// ConverseTech Frontend - Mic Control Component
// ============================================

import { Mic, MicOff, Square } from 'lucide-react';

interface MicControlProps {
  isRecording: boolean;
  isMuted: boolean;
  onStart: () => void;
  onStop: () => void;
  onToggleMute: () => void;
  disabled?: boolean;
}

export default function MicControl({
  isRecording,
  isMuted,
  onStart,
  onStop,
  onToggleMute,
  disabled = false,
}: MicControlProps) {
  return (
    <div data-testid="mic-control" className="flex items-center gap-4">
      {/* Main mic button */}
      {!isRecording ? (
        <button
          data-testid="mic-button"
          onClick={onStart}
          disabled={disabled}
          className={`w-16 h-16 sm:w-16 sm:h-16 rounded-full text-white flex items-center justify-center transition-colors touch-target-lg ${
            disabled
              ? 'bg-gray-600 cursor-not-allowed opacity-50'
              : 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800'
          }`}
        >
          <Mic className="w-8 h-8" />
        </button>
      ) : (
        <>
          {/* Recording indicator - hidden on small screens */}
          <div data-testid="recording-indicator" className="hidden sm:flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></span>
            <span className="text-sm text-gray-400">Recording</span>
          </div>

          {/* Mute button */}
          <button
            data-testid="mute-button"
            onClick={onToggleMute}
            className={`w-14 h-14 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors touch-target ${
              isMuted
                ? 'bg-yellow-600 hover:bg-yellow-700 active:bg-yellow-800'
                : 'bg-gray-600 hover:bg-gray-500 active:bg-gray-400'
            }`}
          >
            {isMuted ? (
              <>
                <MicOff className="w-6 h-6 sm:w-5 sm:h-5" />
                <span data-testid="muted-indicator" className="sr-only">Muted</span>
              </>
            ) : (
              <Mic className="w-6 h-6 sm:w-5 sm:h-5" />
            )}
          </button>

          {/* Stop button */}
          <button
            data-testid="stop-recording"
            onClick={onStop}
            className="w-16 h-16 sm:w-16 sm:h-16 rounded-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white flex items-center justify-center transition-colors touch-target-lg"
          >
            <Square className="w-7 h-7 sm:w-6 sm:h-6 fill-current" />
          </button>
        </>
      )}
    </div>
  );
}
